/**
 * The one audio element of the master player (design release-kit o). The whole app has one, so
 * only one master plays at a time: Play on a second song stops the first one. The player streams
 * the AAC copy of the master through a short-lived link, never the original file.
 */

export type MasterPlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

export interface MasterPlayerState {
  /** The catalog row that plays or is paused. Null when nothing is loaded. */
  itemUuid: string | null;
  status: MasterPlayerStatus;
  positionSec: number;
  /** Null until the audio knows its length. */
  durationSec: number | null;
}

/** Gets a new stream link. The store calls it at Play, and once more after a link error. */
export type MasterLinkLoader = () => Promise<string>;

export const IDLE_MASTER_PLAYER: MasterPlayerState = {
  itemUuid: null,
  status: 'idle',
  positionSec: 0,
  durationSec: null,
};

type Listener = () => void;

/**
 * While a song plays, the store reads the play position on each animation frame and tells the
 * views when it moved this much. The media `timeupdate` event comes only about every 250 ms, so a
 * lyric line that a view marks from it came up to 250 ms late (owner, 2026-10-06). 50 ms keeps the
 * re-renders at about 20 per second.
 */
export const LIVE_POSITION_STEP_SEC = 0.05;

/** Runs a callback on the next frame. A test passes its own. */
export interface FrameScheduler {
  request: (callback: () => void) => number;
  cancel: (id: number) => void;
}

const browserFrames: FrameScheduler = {
  request: (callback) =>
    typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame(() => callback())
      : (setTimeout(callback, 16) as unknown as number),
  cancel: (id) => {
    if (typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(id);
    } else {
      clearTimeout(id);
    }
  },
};

/** A second link error within this time is final. A link lives 1 hour, so a later error is a new expiry. */
const RELINK_WINDOW_MS = 60_000;

/** Only one sound at a time: a master that starts pauses every other sound of the page. */
function pauseOtherMedia(own: HTMLMediaElement): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.querySelectorAll<HTMLMediaElement>('audio, video').forEach((media) => {
    // A muted preview loop makes no sound, so it plays on.
    if (media !== own && !media.paused && !media.muted) {
      media.pause();
    }
  });
}

export class MasterAudioStore {
  private audio: HTMLAudioElement | null = null;

  private state: MasterPlayerState = IDLE_MASTER_PLAYER;

  private readonly listeners = new Set<Listener>();

  private loader: MasterLinkLoader | null = null;

  /**
   * When the store last asked for a new link after an error (ms), or null. One new link per error
   * burst: a second error within RELINK_WINDOW_MS shows the error. A later error (the next link
   * expired after a long session) asks again.
   */
  private relinkedAt: number | null = null;

  /** How many mounted views show each song. The last one to go stops the song. */
  private readonly mounts = new Map<string, number>();

  private otherMediaListener: ((event: Event) => void) | null = null;

  /** The pending animation frame of the position loop, or null. */
  private frame: number | null = null;

  constructor(
    private readonly createAudio: () => HTMLAudioElement = () => new Audio(),
    private readonly frames: FrameScheduler = browserFrames
  ) {}

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getState = (): MasterPlayerState => this.state;

  /**
   * The exact play position of `itemUuid` now, read from the audio element (the state can be one
   * frame old). The sync editor reads it at "Set". Null when the store holds another song.
   */
  livePositionSec = (itemUuid: string): number | null => {
    if (this.state.itemUuid !== itemUuid) {
      return null;
    }
    const { audio } = this;
    if (audio !== null && audio.getAttribute('src') !== null && Number.isFinite(audio.currentTime)) {
      return audio.currentTime;
    }
    return this.state.positionSec;
  };

  /**
   * Plays the song of `itemUuid`. A paused song continues from its position. Another song stops
   * first. `startAtSec` seeks before the first sound (a click on the hook mark of a stopped song).
   */
  async play(itemUuid: string, loader: MasterLinkLoader, startAtSec?: number): Promise<void> {
    const { audio } = this;
    if (audio !== null && this.state.itemUuid === itemUuid && this.state.status === 'paused') {
      if (!audio.getAttribute('src')) {
        // The user paused while the link loaded. The link request still runs and plays the song.
        this.setState({ ...this.state, status: 'loading' });
        return;
      }
      if (startAtSec !== undefined) {
        audio.currentTime = startAtSec;
      }
      await this.startPlayback(audio);
      return;
    }
    if (this.state.itemUuid === itemUuid && (this.state.status === 'loading' || this.state.status === 'playing')) {
      return;
    }
    this.unload();
    this.loader = loader;
    this.relinkedAt = null;
    this.setState({ itemUuid, status: 'loading', positionSec: startAtSec ?? 0, durationSec: null });
    let url: string;
    try {
      url = await loader();
    } catch {
      if (this.state.itemUuid === itemUuid) {
        // A user who paused while the link loaded sees no error. The next Play starts again.
        this.setState(this.state.status === 'paused' ? IDLE_MASTER_PLAYER : { ...this.state, status: 'error' });
      }
      return;
    }
    // The user picked another song while the link loaded.
    if (this.state.itemUuid !== itemUuid) {
      return;
    }
    const element = this.ensureAudio();
    element.src = url;
    if (startAtSec !== undefined) {
      element.currentTime = startAtSec;
    }
    // The user paused while the link loaded: keep the link for the next Play, but do not play.
    if (this.state.status !== 'loading') {
      return;
    }
    await this.startPlayback(element);
  }

  pause(): void {
    // A pause while the song loads cancels the start. The pending play() then sees 'paused'.
    if (this.state.status === 'loading') {
      this.setState({ ...this.state, status: 'paused' });
    }
    this.audio?.pause();
  }

  /** Moves the current song to `seconds`. Does nothing for another song. */
  seek(itemUuid: string, seconds: number): void {
    if (this.audio === null || this.state.itemUuid !== itemUuid) {
      return;
    }
    this.audio.currentTime = seconds;
    this.setState({ ...this.state, positionSec: seconds });
  }

  /** Stops and unloads the song of `itemUuid`. Does nothing for another song. */
  stop(itemUuid: string): void {
    if (this.state.itemUuid !== itemUuid) {
      return;
    }
    this.unload();
    this.setState(IDLE_MASTER_PLAYER);
  }

  /** A view that shows the song mounts. */
  retain(itemUuid: string): void {
    this.mounts.set(itemUuid, (this.mounts.get(itemUuid) ?? 0) + 1);
  }

  /** A view that shows the song unmounts. The last one stops the song. */
  release(itemUuid: string): void {
    const count = (this.mounts.get(itemUuid) ?? 1) - 1;
    if (count > 0) {
      this.mounts.set(itemUuid, count);
      return;
    }
    this.mounts.delete(itemUuid);
    this.stop(itemUuid);
  }

  private async startPlayback(audio: HTMLAudioElement): Promise<void> {
    pauseOtherMedia(audio);
    try {
      await audio.play();
    } catch {
      // The browser refused to play (for example, the link failed before the first byte). The
      // `error` event asks for a new link; a refusal with no error event shows the error here.
      if (this.state.status === 'loading') {
        this.setState({ ...this.state, status: 'error' });
      }
    }
  }

  private ensureAudio(): HTMLAudioElement {
    if (this.audio !== null) {
      return this.audio;
    }
    const audio = this.createAudio();
    audio.preload = 'none';
    audio.addEventListener('playing', () => {
      this.update(audio, 'playing');
      this.startPositionFrames(audio);
    });
    audio.addEventListener('pause', () => {
      this.stopPositionFrames();
      if (this.state.status === 'playing') {
        this.update(audio, 'paused');
      }
    });
    audio.addEventListener('ended', () => {
      this.stopPositionFrames();
      this.setState({ ...this.state, status: 'paused', positionSec: 0 });
      audio.currentTime = 0;
    });
    audio.addEventListener('timeupdate', () => this.update(audio));
    audio.addEventListener('durationchange', () => this.update(audio));
    audio.addEventListener('loadedmetadata', () => this.update(audio));
    audio.addEventListener('error', () => {
      this.onError(audio).catch(console.error);
    });
    this.listenForOtherMedia(audio);
    this.audio = audio;
    return audio;
  }

  /** Another sound of the page starts (a kit video, a voice sample): the master pauses. */
  private listenForOtherMedia(own: HTMLAudioElement): void {
    if (typeof document === 'undefined' || this.otherMediaListener !== null) {
      return;
    }
    this.otherMediaListener = (event: Event) => {
      const target = event.target as HTMLMediaElement | null;
      if (target !== null && target !== own && !target.muted) {
        own.pause();
      }
    };
    // A media `play` event does not bubble, so the listener catches it on the way down.
    document.addEventListener('play', this.otherMediaListener, true);
  }

  /**
   * An expired link answers 403: the store asks for a new link and goes on from the same second.
   * A second error soon after a new link is final.
   */
  private async onError(audio: HTMLAudioElement): Promise<void> {
    const { itemUuid } = this.state;
    if (itemUuid === null || audio.getAttribute('src') === null || audio.getAttribute('src') === '') {
      return;
    }
    const relinkedRecently = this.relinkedAt !== null && Date.now() - this.relinkedAt < RELINK_WINDOW_MS;
    if (relinkedRecently || this.loader === null) {
      this.setState({ ...this.state, status: 'error' });
      return;
    }
    this.relinkedAt = Date.now();
    const at = this.state.positionSec;
    let url: string;
    try {
      url = await this.loader();
    } catch {
      this.setState({ ...this.state, status: 'error' });
      return;
    }
    if (this.state.itemUuid !== itemUuid) {
      return;
    }
    audio.src = url;
    audio.currentTime = at;
    await this.startPlayback(audio);
  }

  /** Reads the play position on each frame while the song plays (`LIVE_POSITION_STEP_SEC`). */
  private startPositionFrames(audio: HTMLAudioElement): void {
    if (this.frame !== null) {
      return;
    }
    const tick = (): void => {
      this.frame = null;
      if (this.audio !== audio || this.state.status !== 'playing') {
        return;
      }
      if (Math.abs(audio.currentTime - this.state.positionSec) >= LIVE_POSITION_STEP_SEC) {
        this.update(audio);
      }
      this.frame = this.frames.request(tick);
    };
    this.frame = this.frames.request(tick);
  }

  private stopPositionFrames(): void {
    if (this.frame !== null) {
      this.frames.cancel(this.frame);
      this.frame = null;
    }
  }

  private update(audio: HTMLAudioElement, status?: MasterPlayerStatus): void {
    if (this.state.itemUuid === null) {
      return;
    }
    const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null;
    this.setState({
      ...this.state,
      status: status ?? this.state.status,
      positionSec: audio.currentTime,
      durationSec: duration ?? this.state.durationSec,
    });
  }

  private unload(): void {
    const { audio } = this;
    this.stopPositionFrames();
    if (audio === null) {
      return;
    }
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    this.loader = null;
    this.relinkedAt = null;
  }

  private setState(next: MasterPlayerState): void {
    this.state = next;
    this.listeners.forEach((listener) => listener());
  }
}

/** The one store of the app. */
export const masterAudio = new MasterAudioStore();
